import { createAppHeader } from '../components/AppHeader';
import {
  loadConferenceIndex,
  loadConferenceCatalog,
  loadReaderAnalysis,
  loadTalk,
} from '../data/conference';
import {
  findThread,
  loadReflections,
  loadThreads,
} from '../data/threads';
import type { RouteLocation } from '../data/schema';
import { createHomePage } from '../features/home/HomePage';
import {
  createReadIndex,
  createTalkReader,
} from '../features/reader/TalkReader';
import { createVerseRoute } from '../features/scriptures/VerseRoute';
import {
  createThreadDetail,
  createThreadsIndex,
} from '../features/threads/ThreadsPage';
import { createSearchPage } from '../features/search/SearchPage';
import { createExploreIndex } from '../features/explore/ExplorePage';
import { createExploreView } from '../features/explore/visualizations';
import { createStudyStore } from '../services/studyStore';
import {
  conferenceIdForTalk,
  createConferenceSelection,
  type ConferenceSelection,
} from '../services/conferenceSelection';
import { StudyStoreError } from '../services/studyStore';
import { setShareMetadata } from '../services/shareMetadata';
import {
  createStudyPage,
  createStudySignIn,
} from '../features/study/StudyPage';
import { findFsyMonth, FSY_MONTHS } from '../data/fsy';
import {
  createFsyIndexPage,
  createFsyMonthPage,
} from '../features/fsy/FsyPages';
import type { BrowserRouter } from './router';

interface RouteCopy {
  kicker: string;
  title: string;
  body: string;
  actionLabel?: string;
  actionHref?: string;
}

function routeCopy(route: RouteLocation): RouteCopy {
  switch (route.name) {
    case 'read-index':
      return {
        kicker: 'Read',
        title: 'Choose a conference talk',
        body: 'Choose any talk to read its complete official text.',
        actionLabel: 'Browse all talks',
        actionHref: '/',
      };
    case 'read-talk':
      return {
        kicker: 'Stable reader route',
        title: route.params.talkId,
        body: 'Loading the complete official talk text.',
        actionLabel: 'Back to conference',
        actionHref: '/',
      };
    case 'read-paragraph':
      return {
        kicker: 'Stable paragraph route',
        title: route.params.talkId,
        body: `Loading source paragraph ${route.params.paragraphId}.`,
        actionLabel: 'Back to conference',
        actionHref: '/',
      };
    case 'scripture':
      return {
        kicker: 'Scripture history',
        title: `${route.params.bookId} ${route.params.chapter}:${route.params.selection}`,
        body: 'Historical citations will appear here with clearly labeled General Conference, Journal of Discourses, and TPJS filters.',
      };
    case 'threads-index':
      return {
        kicker: 'Threads',
        title: 'Guided reading paths',
        body: 'Theme, scripture, and speaker threads will connect talks through stable reading routes.',
        actionLabel: 'Start with a talk',
        actionHref: '/',
      };
    case 'thread':
      return {
        kicker: 'Guided thread',
        title: route.params.threadId.replaceAll('-', ' '),
        body: 'This thread route is stable. Curated excerpts and progress arrive in the guided reading phase.',
        actionLabel: 'Browse all talks',
        actionHref: '/',
      };
    case 'explore-index':
      return {
        kicker: 'Explore',
        title: 'Visual entry points',
        body: 'Six validated visualizations will return as accessible routes into talks, threads, and scriptures.',
      };
    case 'explore-view':
      return {
        kicker: 'Explore view',
        title: route.params.view.replaceAll('-', ' '),
        body: 'This stable visualization route will always lead to a reading destination.',
      };
    case 'search':
      return {
        kicker: 'Search',
        title: 'Find a paragraph',
        body: 'The canonical corpus is ready for paragraph-level lexical and semantic search.',
      };
    case 'study':
      return {
        kicker: 'My Study',
        title: 'Your study journey',
        body: 'Highlights, notes, and progress will sync through the existing server API when study features arrive.',
      };
    case 'fsy-index':
      return {
        kicker: 'Youth Sunday lessons',
        title: 'For the Strength of Youth',
        body: 'Monthly lesson plans connected to April 2026 general conference.',
      };
    case 'fsy-month':
      return {
        kicker: 'For the Strength of Youth',
        title: route.params.monthId,
        body: 'Monthly youth Sunday lessons and related conference passages.',
      };
    case 'not-found':
      return {
        kicker: 'Page not found',
        title: 'This route is not part of Conference Study',
        body: 'Return to the conference home to choose a reading path.',
        actionLabel: 'Go home',
        actionHref: '/',
      };
    case 'home':
      return {
        kicker: 'Conference Study',
        title: 'General Conference',
        body: 'Loading the conference.',
      };
  }
}

function createRouteSurface(route: RouteLocation): HTMLElement {
  const copy = routeCopy(route);
  const wrapper = document.createElement('div');
  wrapper.className = 'route-container page-width';
  wrapper.dataset.route = route.name;

  const article = document.createElement('article');
  article.className = 'route-surface card';
  const kicker = document.createElement('p');
  kicker.className = 'eyebrow eyebrow--gold';
  kicker.textContent = copy.kicker;
  const title = document.createElement('h1');
  title.textContent = copy.title;
  const body = document.createElement('p');
  body.className = 'route-surface__body';
  body.textContent = copy.body;
  article.append(kicker, title, body);
  if (copy.actionLabel && copy.actionHref) {
    const action = document.createElement('a');
    action.href = copy.actionHref;
    action.dataset.router = '';
    action.className = 'button';
    action.textContent = copy.actionLabel;
    article.append(action);
  }
  wrapper.append(article);
  return wrapper;
}

function createLoadingState(): HTMLElement {
  const state = document.createElement('div');
  state.className = 'state-panel page-width';
  state.setAttribute('role', 'status');
  state.textContent = 'Loading conference talks…';
  return state;
}

function createErrorState(): HTMLElement {
  const state = document.createElement('div');
  state.className = 'state-panel state-panel--error page-width';
  state.setAttribute('role', 'alert');
  const title = document.createElement('h1');
  title.textContent = 'Conference talks could not be loaded';
  const body = document.createElement('p');
  body.textContent = 'Check the connection and refresh this page to try again.';
  state.append(title, body);
  return state;
}

function createReaderErrorState(): HTMLElement {
  const state = document.createElement('div');
  state.className = 'state-panel state-panel--error page-width';
  state.dataset.route = 'read-talk';
  state.setAttribute('role', 'alert');
  const title = document.createElement('h1');
  title.textContent = 'This talk could not be opened';
  const body = document.createElement('p');
  body.textContent =
    'Return to the conference list and choose another talk, or refresh to try again.';
  const action = document.createElement('a');
  action.href = '/read';
  action.dataset.router = '';
  action.className = 'button';
  action.textContent = 'Browse talks';
  state.append(title, body, action);
  return state;
}

function createThreadsErrorState(): HTMLElement {
  const state = document.createElement('div');
  state.className = 'state-panel state-panel--error page-width';
  state.dataset.route = 'threads-index';
  state.setAttribute('role', 'alert');
  const title = document.createElement('h1');
  title.textContent = 'Reading threads could not be loaded';
  const body = document.createElement('p');
  body.textContent =
    'Return to the Threads index or refresh this page to try again.';
  const action = document.createElement('a');
  action.href = '/threads';
  action.dataset.router = '';
  action.className = 'button';
  action.textContent = 'Browse threads';
  state.append(title, body, action);
  return state;
}

function createFooter(): HTMLElement {
  const footer = document.createElement('footer');
  footer.className = 'app-footer';
  footer.textContent = 'Conference Study';
  return footer;
}

function conferenceFooter(title: string): string {
  return `Conference Study · ${title} · Official talk text remains attributed to The Church of Jesus Christ of Latter-day Saints. Thread introductions, connection notes, reflection questions, theme analysis, and sentiment arcs are AI-generated study aids.`;
}

const FSY_FOOTER_COPY =
  'FSY lesson content is from For the Strength of Youth, published by The Church of Jesus Christ of Latter-day Saints. Related conference excerpts link to their source paragraphs.';

export function createAppShell(router: BrowserRouter): HTMLElement {
  const shell = document.createElement('div');
  shell.className = 'app-shell';
  const skipLink = document.createElement('a');
  skipLink.className = 'skip-link';
  skipLink.href = '#main-content';
  skipLink.textContent = 'Skip to main content';
  const headerSlot = document.createElement('div');
  const main = document.createElement('main');
  main.id = 'main-content';
  main.tabIndex = -1;
  const footer = createFooter();
  const studyStore = createStudyStore();
  const catalogPromise = loadConferenceCatalog();
  let conferenceSelection: ConferenceSelection | undefined;
  let renderVersion = 0;

  const render = async (route: RouteLocation): Promise<void> => {
    const version = ++renderVersion;
    let catalog;
    try {
      catalog = await catalogPromise;
    } catch {
      if (version === renderVersion) main.replaceChildren(createErrorState());
      return;
    }
    if (version !== renderVersion) return;
    conferenceSelection ??= createConferenceSelection(catalog, localStorage);
    const linkedConferenceId =
      route.name === 'read-talk' || route.name === 'read-paragraph'
        ? conferenceIdForTalk(catalog, route.params.talkId)
        : undefined;
    if (linkedConferenceId && linkedConferenceId !== conferenceSelection.currentId) {
      conferenceSelection.select(linkedConferenceId);
    }
    const conferenceId = conferenceSelection.currentId;
    const activeConference = catalog.conferences.find(
      (conference) => conference.conferenceId === conferenceId,
    );
    headerSlot.replaceChildren(
      createAppHeader(route, catalog, conferenceId, (nextConferenceId) => {
        conferenceSelection?.select(nextConferenceId);
        if (route.name === 'read-talk' || route.name === 'read-paragraph') {
          router.navigate('/read');
        } else {
          void render(router.current);
        }
      }),
    );
    setShareMetadata({
      title: route.name === 'home' && activeConference
        ? activeConference.title
        : routeCopy(route).title,
      description: route.name === 'home' && activeConference
        ? `Study all ${String(activeConference.talkCount)} talks from ${activeConference.title}.`
        : routeCopy(route).body,
      pathname: route.pathname,
    });
    footer.textContent = route.name.startsWith('fsy-')
      ? FSY_FOOTER_COPY
      : conferenceFooter(activeConference?.title ?? 'General Conference');
    if (
      route.name !== 'home' &&
      route.name !== 'read-index' &&
      route.name !== 'read-talk' &&
      route.name !== 'read-paragraph' &&
      route.name !== 'scripture' &&
      route.name !== 'threads-index' &&
      route.name !== 'thread' &&
      route.name !== 'search' &&
      route.name !== 'explore-index' &&
      route.name !== 'explore-view' &&
      route.name !== 'study' &&
      route.name !== 'fsy-index' &&
      route.name !== 'fsy-month'
    ) {
      main.replaceChildren(createRouteSurface(route));
      return;
    }

    main.replaceChildren(createLoadingState());
    try {
      if (route.name === 'fsy-index') {
        main.replaceChildren(createFsyIndexPage(FSY_MONTHS));
        return;
      }
      if (route.name === 'fsy-month') {
        const month = findFsyMonth(route.params.monthId);
        if (!month) {
          main.replaceChildren(createRouteSurface(route));
          return;
        }
        main.replaceChildren(createFsyMonthPage(month, new Date(), FSY_MONTHS));
        document.title = `${month.monthLabel} FSY | Conference Study`;
        return;
      }
      if (route.name === 'search') {
        const searchPage = await createSearchPage(conferenceId);
        if (version === renderVersion) {
          main.replaceChildren(searchPage);
        }
        return;
      }
      if (route.name === 'explore-index') {
        main.replaceChildren(createExploreIndex());
        return;
      }
      if (route.name === 'explore-view') {
        const index = await loadConferenceIndex(conferenceId);
        if (version === renderVersion) {
          main.replaceChildren(createExploreView(route.params.view, index));
        }
        return;
      }
      if (route.name === 'study') {
        try {
          const [study, index, threads] = await Promise.all([
            studyStore.loadStudy(conferenceId),
            loadConferenceIndex(conferenceId),
            loadThreads(conferenceId),
          ]);
          if (version === renderVersion) {
            const belongsToConference = (talkId: string): boolean =>
              talkId.startsWith(`${conferenceId}-`);
            main.replaceChildren(
              createStudyPage(
                {
                  ...study,
                  highlights: study.highlights.filter((item) => belongsToConference(item.talkId)),
                  notes: study.notes.filter((item) => belongsToConference(item.talkId)),
                  readingProgress: study.readingProgress.filter((item) => belongsToConference(item.talkId)),
                  bookmarks: study.bookmarks.filter((item) => belongsToConference(item.talkId)),
                  threadProgress: study.threadProgress.filter(
                    (item) => item.conferenceId === conferenceId,
                  ),
                },
                index,
                threads,
              ),
            );
          }
        } catch (error) {
          if (version !== renderVersion) return;
          if (error instanceof StudyStoreError && error.status === 401) {
            main.replaceChildren(createStudySignIn());
            return;
          }
          throw error;
        }
        return;
      }
      if (route.name === 'scripture') {
        main.replaceChildren(
          createVerseRoute(
            route.params.bookId,
            route.params.chapter,
            route.params.selection,
            router,
            studyStore,
          ),
        );
        return;
      }
      if (route.name === 'threads-index' || route.name === 'thread') {
        const [threads, readingProgress, threadProgress] = await Promise.all([
          loadThreads(conferenceId),
          studyStore.loadProgress().catch(() => []),
          studyStore.loadThreadProgress(conferenceId).catch(() => []),
        ]);
        if (version !== renderVersion) return;
        if (route.name === 'threads-index') {
          main.replaceChildren(
            createThreadsIndex(
              threads,
              readingProgress,
              threadProgress.filter(
                (progress) => progress.conferenceId === conferenceId,
              ),
            ),
          );
          return;
        }
        const thread = findThread(threads, route.params.threadId);
        if (!thread) {
          main.replaceChildren(createThreadsErrorState());
          return;
        }
        main.replaceChildren(
          createThreadDetail(
            thread,
            threads,
            readingProgress,
            threadProgress.filter(
              (progress) => progress.conferenceId === conferenceId,
            ),
            conferenceId,
            studyStore,
          ),
        );
        document.title = `${thread.title} | Conference Study`;
        return;
      }
      if (route.name === 'read-index') {
        const index = await loadConferenceIndex(conferenceId);
        if (version === renderVersion) {
          main.replaceChildren(createReadIndex(index));
        }
        return;
      }
      if (route.name === 'read-talk' || route.name === 'read-paragraph') {
        const activeThreadId = new URLSearchParams(
          window.location.search,
        ).get('thread');
        const [index, talk, analysis, reflections, threads] = await Promise.all([
          loadConferenceIndex(conferenceId),
          loadTalk(route.params.talkId, conferenceId),
          loadReaderAnalysis(conferenceId),
          loadReflections(conferenceId),
          activeThreadId ? loadThreads(conferenceId) : Promise.resolve(undefined),
        ]);
        if (version === renderVersion) {
          const activeThread =
            activeThreadId && threads
              ? findThread(threads, activeThreadId)
              : undefined;
          main.replaceChildren(
            createTalkReader({
              talk,
              index,
              analysis: analysis.talks[talk.talkId],
              reflection: reflections.talks[talk.talkId],
              activeThread:
                activeThread?.steps.some(
                  (step) => step.talkId === talk.talkId,
                ) === true
                  ? activeThread
                  : undefined,
              targetParagraphId:
                route.name === 'read-paragraph'
                  ? route.params.paragraphId
                  : undefined,
              router,
              studyStore,
            }),
          );
        }
        return;
      }
      const [index, readingProgress] = await Promise.all([
        loadConferenceIndex(conferenceId),
        studyStore.loadProgress().catch(() => []),
      ]);
      if (version === renderVersion) {
        main.replaceChildren(createHomePage(index, readingProgress));
      }
    } catch {
      if (version === renderVersion) {
        main.replaceChildren(
          route.name === 'home'
            ? createErrorState()
            : route.name === 'threads-index' || route.name === 'thread'
              ? createThreadsErrorState()
              : createReaderErrorState(),
        );
      }
    }
  };

  router.subscribe((route) => {
    void render(route);
  });
  shell.append(skipLink, headerSlot, main, footer);
  return shell;
}
